import * as React from 'react';
import {useMergeRefs} from '@floating-ui/react';

import {CREATABLE_OPTION_VALUE} from '../../hooks';
import {mergeProps} from '../../utils';
import {type TextInputProps} from '../TextInput';

import {
  useSearchableListContext,
  useSearchableListIndexContext,
  useSearchableListSelectionContext,
} from './useSearchableList';

type UseSearchableListInputProps = {
  ref: React.ForwardedRef<HTMLInputElement>;
} & TextInputProps;

export const useSearchableListInput = ({
  isDisabled: isDisabledProp,
  ref,
  ...props
}: UseSearchableListInputProps) => {
  const {getReferenceProps, handleInputChange, inputValue, refs} =
    useSearchableListContext();

  const {activeIndex, setActiveIndex, flattenedOptions} =
    useSearchableListIndexContext();
  const {
    inputRef,
    isDisabled: isSearchableListDisabled,
    onCreateOption,
    toggleSelectedValue,
  } = useSearchableListSelectionContext();

  const mergedRefs = useMergeRefs([ref, inputRef]);

  const referenceProps = getReferenceProps({
    ref: mergedRefs,
    onBlur: (e) => {
      const listIndex = e.relatedTarget
        ?.closest('[data-list-index]')
        ?.getAttribute('data-list-index');

      if (Number(listIndex) === activeIndex) {
        // The click target is within the activeIndex item.
        return;
      }
      setActiveIndex?.(null);
    },
    onChange: handleInputChange,
    value: inputValue,
    'aria-autocomplete': 'list',
    onKeyDown(event) {
      switch (event.key) {
        case 'Enter': {
          /**
           * Prevent form submission on Enter key press
           */
          event.preventDefault();

          /**
           * Select the activeIndex
           */
          if (activeIndex !== null) {
            const activeItem = refs.floating.current?.querySelector(
              `[data-list-index="${activeIndex}"] [data-value]`
            );

            if (!activeItem) {
              return;
            }

            const option = flattenedOptions[activeIndex];

            const isActiveIndexDisabled = activeItem?.ariaDisabled === 'true';
            if (!isActiveIndexDisabled) {
              if (option?.value === CREATABLE_OPTION_VALUE) {
                onCreateOption?.(inputValue?.trim() ?? '');
              } else {
                toggleSelectedValue(
                  activeItem?.getAttribute('data-value') ?? '',
                  {
                    option,
                  }
                );
              }
            }
          }
          break;
        }
        case 'Escape':
          if (inputValue) {
            /**
             * Reset the activeIndex
             */
            setActiveIndex?.(null);

            /**
             * Clear the TextInput's value
             */
            handleInputChange?.({
              target: {value: ''},
            } as React.ChangeEvent<HTMLInputElement>);
          }
          break;
        default: {
          return;
        }
      }
    },
  });

  const isDisabled = isDisabledProp || isSearchableListDisabled;

  const mergedProps = mergeProps(referenceProps, {
    autoComplete: 'off',
    isDisabled,
    ...props,
  });

  return React.useMemo(() => ({...mergedProps}), [mergedProps]);
};
